type JsonResponse = { response: Response; data: unknown };
const pendingReads = new Map<string, Promise<JsonResponse>>();
const READ_TIMEOUT_MS = 12_000;

/** Coalesce concurrent reads within one identity; retry transient reads once.
 * Writes are always sent once, with their existing idempotency semantics.
 * No response cache is retained after a read settles.
 */
export function fetchWmJson(url: string, init: RequestInit): Promise<JsonResponse> {
  const read = (init.method || 'GET').toUpperCase() === 'GET';
  const headers = Array.from(new Headers(init.headers).entries()).sort(([a], [b]) => a.localeCompare(b));
  const key = read && !init.signal ? JSON.stringify([url, headers, init.credentials]) : null;
  if (key && pendingReads.has(key)) return pendingReads.get(key)!;
  const run = async (): Promise<JsonResponse> => {
    for (let attempt = 0; ; attempt++) {
      const controller = new AbortController();
      const abort = () => controller.abort(init.signal?.reason);
      if (init.signal?.aborted) abort();
      else init.signal?.addEventListener('abort', abort, { once: true });
      const timer = read ? setTimeout(() => controller.abort(), READ_TIMEOUT_MS) : null;
      try {
        const response = await fetch(url, { ...init, signal: controller.signal, ...(read ? { cache: 'no-store' as const } : {}) });
        let data: unknown;
        try { data = await response.json(); } catch {
          if (read && response.ok) throw new Error('Could not load waste management. Try again.');
          data = {};
        }
        if (read && attempt === 0 && [408, 429, 500, 502, 503, 504].includes(response.status)) continue;
        return { response, data };
      } catch (error) {
        if (init.signal?.aborted) throw error;
        if (read && attempt === 0) continue;
        if (controller.signal.aborted) throw new Error('Waste management is taking longer than expected. Try again.');
        throw error;
      } finally {
        if (timer != null) clearTimeout(timer);
        init.signal?.removeEventListener('abort', abort);
      }
    }
  };
  // Let a transient cold start recover without overlapping identical requests.
  const promise = run().finally(() => { if (key) pendingReads.delete(key); });
  if (key) pendingReads.set(key, promise);
  return promise;
}
